"use client";

import type { Dispatch, SetStateAction, MutableRefObject } from "react";
import type { ChatMessage, MapLayer } from "@/lib/types";

// Small helper: append to the last part of a given type, or create it
export function appendDeltaIntoParts(
  message: ChatMessage,
  kind: "text" | "reasoning",
  chunk: string
): ChatMessage {
  const parts = message.parts ? [...message.parts] : [];
  // Find last index of that kind
  let i = parts.length - 1;
  while (i >= 0 && parts[i]?.type !== kind) i--;

  if (i >= 0) {
    const p = parts[i] as any;
    const prev = (p.text ?? "") as string;
    parts[i] = { ...p, type: kind, text: prev + chunk };
  } else {
    parts.push({ type: kind, text: chunk } as any);
  }
  return { ...message, parts };
}

type StreamPart =
  | { type: "data-appendMessage"; data: string }
  | { type: "data-streamPart"; data: string }
  | { type: "data-tool-processing"; data: string }
  | { type: "data-map-artifacts"; data: string }
  | { type: "data-usage"; data: any }
  | { type: "suggested_actions"; data: string[] } // not used
  | { type: string; data?: unknown };

type HandleStreamArgs = {
  dataPart: StreamPart;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setUsage: (u: any) => void;
  setActiveSuggestions: (suggestions: string[]) => void; 
  currentAssistantIdRef: MutableRefObject<string | null>;
  tempAssistantIdRef: MutableRefObject<string | null>;
  addMapLayers: (layers: MapLayer[]) => void;
};

export function handleOnData({
  dataPart,
  setMessages,
  setUsage,
  setActiveSuggestions,
  currentAssistantIdRef,
  tempAssistantIdRef,
  addMapLayers,
}: HandleStreamArgs) {
  
  // Handle each type explicitly and return early
  const eventType = dataPart.type;
  
  // Not used, was making the process the break
  // We moved the logic inside the 4) User/Assistant Messages
  // 1) Suggestions - handle FIRST 
  if (eventType === "suggested_actions") {
    if (Array.isArray(dataPart.data)) {
      setActiveSuggestions(dataPart.data);
    }
    return; 
  }

  // 2) Map artifacts
  if (eventType === "data-map-artifacts" && typeof dataPart.data === "string") {
    try {
      const artifactData = JSON.parse(dataPart.data) as {
        chatId: string;
        artifacts: MapLayer[];
      };

      const validArtifacts = artifactData.artifacts.filter((artifact) => {
        return artifact.specification?.layers?.length > 0 && artifact.cachedData != null;
      });      
      
      if (validArtifacts.length > 0) {
        // Log the event
        console.log("Live Map Update:", validArtifacts[0].id);
        
        // CRITICAL: We pass the artifacts to addMapLayers. 
        // Ensure your use-map.ts 'addLayers' function is designed 
        // to replace a layer if the ID already exists.
        addMapLayers(validArtifacts);
      }
    } catch (err) {
      console.error("Failed to parse map artifacts:", err);
    }
    return;
  }

// 2.5) Tool Processing (new!)
if (eventType === "data-tool-processing" && typeof dataPart.data === "string") {
  try {
    const toolName = dataPart.data.replace("Executing: ", "").trim();
    
    // Optional: Log it for debugging
    console.log(`[TOOL] Executing: ${toolName}`);
    
    // Option A: Keep ThinkingMessage visible (recommended for now)
    // Do nothing special here — your global {status !== "ready" && <ThinkingMessage />} 
    // will already stay mounted because status is still "in_progress"
    
    // Option B: Update UI to show current tool (better UX)
    // You can set a state like currentTool: string
    // setCurrentTool(toolName);
    // Then in ThinkingMessage: show "Executing: ${currentTool}..." instead of generic "Thinking..."
    
    // For ultra-quick patch: just ensure we don't hide anything prematurely
    // (your existing global ThinkingMessage condition already handles this)
    
    // If you want to force a message update:
    setMessages((prev) => {
      const lastMsg = prev[prev.length - 1];
      if (lastMsg?.role === "assistant" && lastMsg.parts?.length === 0) {
        // Append a temporary "tool" part so the assistant bubble appears early
        return prev.map((m) =>
          m.id === lastMsg.id
            ? {
                ...m,
                parts: [
                  ...(m.parts || []),
                  { type: "text", text: `Executing tool: ${toolName}...` },
                ],
              }
            : m
        );
      }
      return prev;
    });
  } catch (err) {
    console.error("Failed to handle tool_processing:", err);
  }
  return;
}

  // 3) Usage
  if (eventType === "data-usage") {
    setUsage(dataPart.data);
    return; // CRITICAL: return early
  }

  // 4) User/Assistant Messages
  if (eventType === "data-appendMessage" && typeof dataPart.data === "string") {
    try {
      const incoming = JSON.parse(dataPart.data) as ChatMessage & { suggestions?: string[] };

      // Extract suggestions if present
      if (incoming.suggestions && Array.isArray(incoming.suggestions)) {
        console.log("Extracted suggestions from message:", incoming.suggestions);
        setActiveSuggestions(incoming.suggestions);
      }

      setMessages((prev) => {
        // User message: simple de-dupe
        if (incoming.role === "user") {
          return prev.some((x) => x.id === incoming.id && x.role === "user")
            ? prev
            : [...prev, incoming];
        }

        // Assistant message logic
        if (incoming.role === "assistant") {
          const tempId = tempAssistantIdRef.current;

          // If replacing a temporary streaming message
          if (tempId) {
            const idx = prev.findIndex((x) => x.id === tempId && x.role === "assistant");
            if (idx !== -1) {
              const streamed = prev[idx];
              // Merge parts to ensure we don't lose client-side state if any
              const merged: ChatMessage = {
                ...incoming,
                parts: (incoming.parts?.length ? incoming.parts : streamed.parts) ?? [],
              };
              const copy = [...prev];
              copy[idx] = merged;
              currentAssistantIdRef.current = incoming.id;
              tempAssistantIdRef.current = null;
              return copy;
            }
          }

          // If assistant already present by final id? Replace it
          const j = prev.findIndex((x) => x.id === incoming.id && x.role === "assistant");
          if (j !== -1) {
            const copy = [...prev];
            copy[j] = { ...copy[j], ...incoming };
            currentAssistantIdRef.current = incoming.id;
            return copy;
          }

          // Otherwise insert fresh assistant
          currentAssistantIdRef.current = incoming.id;
          return [...prev, { ...incoming, parts: incoming.parts ?? [] }];
        }

        return prev;
      });
    } catch (err) {
      console.error("Failed to parse data-appendMessage:", err);
    }
    return;
  }

  // 5) Streaming Deltas
if (eventType === "data-streamPart" && typeof dataPart.data === "string") {
  try {
    const chunk = JSON.parse(dataPart.data) as
      | { type: "text-delta"; textDelta: string }
      | { type: "reasoning"; reasoning: string };

    // CRITICAL FIX: Always check if the last message is still a user message
    // If yes → this is a brand new assistant turn → force new temp ID
    setMessages((prev) => {
      const lastMsg = prev[prev.length - 1];
      let currentTargetId = currentAssistantIdRef.current;

      // If last message is user OR no assistant exists yet → new turn
      if (!currentTargetId || lastMsg?.role === "user") {
        currentTargetId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; // unique per turn
        currentAssistantIdRef.current = currentTargetId;
        tempAssistantIdRef.current = currentTargetId;

        // Create fresh temp assistant
        return [
          ...prev,
          {
            id: currentTargetId,
            role: "assistant",
            parts: [],
            createdAt: new Date().toISOString(),
          } as ChatMessage,
        ];
      }

      return prev;
    });

    // Now append delta to the current (fresh) target
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== currentAssistantIdRef.current || m.role !== "assistant") return m;

        if (chunk.type === "text-delta") {
          return appendDeltaIntoParts(m, "text", chunk.textDelta);
        }
        if (chunk.type === "reasoning") {
          return appendDeltaIntoParts(m, "reasoning", chunk.reasoning);
        }
        return m;
      })
    );
  } catch (err) {
    console.error("Failed to parse data-streamPart:", err);
  }
  return;
}

  // 6) Ignore unknown types
  console.log("Unhandled event type:", eventType);
}
